import { ToastMessageOptions } from 'primeng/api';

import { ODataMessage } from "./message";
import { MassDeleteLabels } from '../../components/mass-delete-refusals/mass-delete-refusals.model';

/** Code de la synthèse rendue par le serveur — cf. `_notifyMassDeleteSummary`. */
export const MASS_DELETE_SUMMARY_CODE = 'MASS_DELETE_SUMMARY';

/**
 * Un objet que le serveur a refusé de supprimer.
 *
 * `label` est le nom que l'objet porte à l'écran (la référence d'un document, à défaut
 * son libellé). Il peut être nul : un objet devenu illisible — supprimé entre-temps, ou
 * hors du périmètre du dossier — n'a plus de libellé à rendre, seul son id subsiste.
 */
export interface MassDeleteRefusal {
    id: number;
    label: string | null;
    messages: ODataMessage[];
}

/**
 * Réponse d'une suppression groupée (`POST .../mass-delete/`).
 *
 * ⚠️ Ce n'est **pas** l'enveloppe OData habituelle : `deleted`, `refused` et `pending`
 * sont à la racine du corps, et non sous `value`. D'où cette classe plutôt que
 * `ODataResponse`.
 *
 * Contrat complet : `b4cash-srv/documentation/architecture/MASS_DELETE.md` §5.
 */
export class MassDeleteResponse {

    /** La synthèse du serveur, dont `MASS_DELETE_INCOMPLETE` quand `pending` est garni. */
    messages: ODataMessage[] = [];

    /** Les ids réellement supprimés. */
    deleted: number[] = [];

    /** Les objets refusés, avec le motif qu'aurait rendu une suppression unitaire. */
    refused: MassDeleteRefusal[] = [];

    /**
     * Les ids que le serveur n'a **pas examinés**, faute de temps : ni supprimés, ni
     * refusés. Le lot a été interrompu, pas refusé — c'est au front de reproposer la
     * suite en recochant ces lignes après le rechargement de la liste.
     */
    pending: number[] = [];

    constructor(jsonResponse: any) {
        for (var key in jsonResponse?.messages) {
            this.messages.push(new ODataMessage(jsonResponse.messages[key]));
        }

        this.deleted = jsonResponse?.deleted ?? [];
        this.pending = jsonResponse?.pending ?? [];

        for (var key in jsonResponse?.refused) {
            const refusal = jsonResponse.refused[key];
            let messages: ODataMessage[] = [];
            for (var messageKey in refusal?.messages) {
                messages.push(new ODataMessage(refusal.messages[messageKey]));
            }
            this.refused.push({
                id: refusal.id,
                label: refusal.label ?? null,
                messages: messages
            });
        }
    }
}

/**
 * Compose « 4 factures supprimées », « 1 devis refusé » : le nom s'accorde en nombre, le
 * participe en genre et en nombre, pour que le singulier et le pluriel donnent la même
 * phrase.
 *
 * @param count Le nombre d'objets concernés
 * @param participle Le participe au masculin singulier — « supprimé », « refusé »
 * @param labels Comment nommer ces objets
 */
export function massDeleteCountLabel(count: number, participle: string, labels: MassDeleteLabels): string {
    const noun = count > 1 ? labels.plural : labels.singular;
    return `${count} ${noun} ${participle}${labels.feminine ? 'e' : ''}${count > 1 ? 's' : ''}`;
}

/**
 * La notification de synthèse d'une suppression groupée.
 *
 * La synthèse du serveur n'est volontairement pas relayée : elle est classée en succès
 * quel que soit le résultat, ce qui afficherait un bandeau vert « 0 supprimé(s),
 * 5 refusé(s) ». On reprend ici la forme retenue pour le plan comptable (#1030) — un
 * succès quand tout est parti, un avertissement sinon, qui distingue la suppression
 * partielle de l'absence totale de suppression.
 *
 * Partagée entre `TableBaseComponent.massDelete()` et les écrans qui suppriment une
 * sélection implicite hors table de base (#1115) : une seule phrase, quel que soit l'écran.
 *
 * @param response Le rapport du serveur
 * @param labels Comment nommer les objets supprimés
 */
export function massDeleteSummaryMessage(response: MassDeleteResponse, labels: MassDeleteLabels): ToastMessageOptions {
    const deleted = response.deleted.length;
    const refused = response.refused.length;
    const deletedLabel = massDeleteCountLabel(deleted, 'supprimé', labels);
    const refusedLabel = massDeleteCountLabel(refused, 'refusé', labels);

    if (refused === 0) {
        return {
            severity: 'success',
            summary: 'Suppression terminée',
            detail: deletedLabel,
            life: 2000
        };
    }

    return {
        severity: 'warn',
        summary: deleted === 0 ? 'Aucune suppression effectuée' : 'Suppression partielle',
        detail: deleted === 0 ? refusedLabel : `${deletedLabel}, ${refusedLabel}`,
        life: 10000
    };
}
